<template>
    <div>
        <div class="container content-page-wrapper projects-edit pb-5" v-if="Object.keys(projectFields).length !== 0 && authenticated" :class="{'loading': loading}">
            <div class="row pt-5 pb-3" v-if="!editProject">
                <div class="col-12 m-0">
                    <h1>{{ projectFields.title }}</h1>
                    <p class="lead mb-0">(Nr. {{ projectFields.custom_project_id }} | {{ projectFields.user.name }}) <project-invalid v-if="$project_plannings_need_update(projectFields.plannings)" description="This project contains invalid configurations that have been created before the last major update of the product configurator and can not be used anymore." separator="before" /></p>
                    <router-link :to="{ name: 'projects.index' }" tag="button" class="btn btn-sm btn-light" v-if="projectFields.user_id === user.id"><i class="fas fa-chevron-left"></i>
                        {{ $t('Project overview') }}</router-link>
                    <router-link :to="{ name: 'users.show', params: { id: projectFields.user_id } }" tag="button" class="btn btn-sm btn-light" v-if="projectFields.user_id !== user.id"><i class="fas fa-chevron-left"></i> {{ $t('Back to customer') }}</router-link>
                </div>
            </div>
            <div class="row pb-3" v-if="!editProject">
                <div class="col-12 col-lg-6 m-0" v-if="projectFields.plannings.length > 0">
                    <!-- Export -->
                    <div class="dropdown d-inline-block">
                        <button class="btn btn-light btn-sm dropdown-toggle" type="button" :id="'project-export-button-' + this.$route.params.id" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                            <i class="fas fa-cloud-download-alt"></i> Export
                        </button>
                        <div class="dropdown-menu" :aria-labelledby="'project-export-button-' + this.$route.params.id">
                            <download-pdf-button
                                :url="'/pdf/projects/' + this.$route.params.id"
                                :button_class="'dropdown-item'" />
                            <download-excel-button
                                :disabled="$project_plannings_need_update(projectFields.plannings)"
                                :url="'/excel/projects/' + this.$route.params.id"
                                :button_class="'dropdown-item'" />
                            <download-excel-button v-if="$can('download_mounting_rail_labels_data')"
                                :button_text="$t('Export labels')"
                                :price_option="false"
                                :production_time_option="false"
                                :url="'/excel/projects/' + this.$route.params.id + '/labels'"
                                :button_class="'dropdown-item'" />
                            <download-dialux-data-button
                                v-if="projectFields.plannings && projectFields.plannings.length > 0"
                                :plannings="projectFields.plannings"
                                :button_class="'dropdown-item'"
                                @update-loading="updateLoading" />
                        </div>
                    </div>
                    <!-- Edit -->
                    <div class="dropdown d-inline-block">
                        <button class="btn btn-light btn-sm dropdown-toggle" type="button" :id="'project-edit-button-' + this.$route.params.id" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                            <i class="far fa-edit"></i> {{ $t('Edit project') }}
                        </button>
                        <div class="dropdown-menu" :aria-labelledby="'project-edit-button-' + this.$route.params.id">
                            <button class="btn btn-sm btn-light dropdown-item" @click.prevent="activateEditProject"
                                    v-if="(!projectFields.project_quote && $can('edit_projects')) || projectFields.user_id === user.id">
                                {{ $t('Edit project data') }}</button>
                            <button class="btn btn-sm btn-light dropdown-item" @click.prevent="activateUpdateProjectUser"
                                    v-if="$can('update_project_user_relation')">
                                {{ $t('Change project owner') }}
                            </button>
                        </div>
                    </div>
                    <!-- Duplicate -->
                    <button class="btn btn-sm btn-light" @click.prevent="duplicateProject(projectFields.id)"
                            :disabled="$project_plannings_need_update(projectFields.plannings) || projectDuplicating || (projectFields.user_id !== user.id && !$can('edit_all_projects'))">
                        <i class="far fa-clone" title="Duplizieren"></i> <span>{{ $t('Duplicate') }}</span>
                    </button>
                </div>
                <div class="col-12 col-lg-6 m-0 text-right" v-if="projectFields.plannings.length > 0">
                    <button class="btn btn-sm btn-primary" @click.prevent="updateDeletionDeadline" :disabled="loading" v-if="!projectFields.project_finished && projectOlderThan90Days"><i class="fa-solid fa-arrows-rotate"></i> {{ $t('Prolong automatic project finish') }}</button>

                    <div class="dropdown d-inline-block">
                        <button class="btn btn-primary btn-sm dropdown-toggle" type="button" :id="'project-status-button-' + this.$route.params.id" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                            <i class="fas fa-chart-line"></i> {{ $t('Project status') }}
                        </button>
                        <div class="dropdown-menu" :aria-labelledby="'project-status-button-' + this.$route.params.id">
                            <!-- Reset project Status -->
                            <button class="btn btn-sm dropdown-item" @click.prevent="resetProjectStatus" :disabled="loading" v-if="$can('reset_project_status')"><i class="fa-regular fa-arrows-rotate"></i> {{ $t('Reset project status to active') }}</button>
                            <!-- Request Quote -->
                            <button class="btn btn-sm dropdown-item" @click.prevent="requestQuote" :disabled="$project_plannings_need_update(projectFields.plannings) || requestQuoteSubmitting || projectFields.user_id !== user.id" v-if="!projectFields.project_quote && !projectFields.project_ordered && !projectFields.project_finished"><i class="far fa-paper-plane"></i> {{ requestQuoteSubmitting ? $t('Requesting offer') + '...' : $t('Request offer') }}</button>
                            <button class="btn btn-sm bg-success dropdown-item" disabled="true" @click.prevent v-else-if="!projectFields.project_ordered && projectFields.project_quote">{{ $t('Offer requested') }}: {{ projectFields.project_quote }}</button>
                            <!-- Order project -->
                            <button class="btn btn-sm dropdown-item" @click.prevent="submitOrder" :disabled="$project_plannings_need_update(projectFields.plannings) || submitOrderSubmitting || projectFields.user_id !== user.id" v-if="!projectFields.project_ordered && !projectFields.project_finished && $can('order_project')"><i class="far fa-paper-plane"></i> {{ submitOrderSubmitting ? $t('Sending order') + '...' : $t('Send order') }}</button>
                            <button class="btn btn-sm bg-success dropdown-item" disabled="true" @click.prevent v-else-if="projectFields.project_ordered && $can('order_project')">{{ $t('Project ordered') }}: {{ projectFields.project_ordered }}</button>
                            <!-- Finish project -->
                            <button class="btn btn-sm dropdown-item" @click.prevent="setProjectAsFinished" :disabled="loading" v-if="$can('finish_project') && !projectFields.project_finished"><i class="fas fa-check"></i> {{ $t('Mark as finished') }}</button>
                            <button class="btn btn-sm bg-success dropdown-item" disabled="true" @click.prevent v-else-if="projectFields.project_finished">{{ $t('Project finished') }}: {{ projectFields.project_finished }}</button>
                        </div>
                    </div>
                </div>
            </div>
            <div class="row py-5" v-if="editProject && $can('edit_projects')">
                <div class="col-12 m-0">
                    <ValidationObserver v-slot="{ handleSubmit }" >
                        <form @submit.prevent="handleSubmit(updateProject)" class="create-project-form">
                            <ValidationProvider
                                :name="$t('Project name')"
                                rules="required"
                                tag="div"
                                class="form-group"
                                v-slot="{ errors }">
                                    <label class="lead">{{ $t('Project name') }}</label>
                                    <input v-model="projectFields.title" type="text" name="title" class="form-control">
                                    <span class="text-danger">{{ errors[0] }}</span>
                                    <div class="alert alert-danger" v-if="projectFormErrors && projectFormErrors.title">
                                        {{ projectFormErrors.title[0] }}
                                    </div>
                            </ValidationProvider>

                            <button type="submit" class="btn btn-primary" :disabled="projectFormSubmitting">{{ projectFormSubmitting ? $t('Save...') : $t('Save') }}</button>
                            <button class="btn text-light" :disabled="projectFormSubmitting" @click.prevent="deactivateEditProject">{{ $t('Cancel') }}</button>
                        </form>
                    </ValidationObserver>
                </div>
            </div>
            <div class="row py-5" v-if="editProjectUser && $can('update_project_user_relation')">
                <div class="col-12 m-0">
                    <ValidationObserver v-slot="{ handleSubmit }" >
                        <form @submit.prevent="handleSubmit(updateProjectUser)" class="create-project-form">
                            <ValidationProvider
                                :name="$t('Project owner')"
                                rules="required"
                                tag="div"
                                class="form-group"
                                v-slot="{ errors }">
                                <label class="lead">{{ $t('Change project owner') }}</label>
                                <Select2 v-model="projectFields.user_id" :options="users" @change="select2Change($event)" @select="select2Selected($event)" />
                                <span class="text-danger">{{ errors[0] }}</span>
                                <div class="alert alert-danger" v-if="projectFormErrors && projectFormErrors.user_id">
                                    {{ projectFormErrors.user_id[0] }}
                                </div>
                            </ValidationProvider>

                            <button type="submit" class="btn btn-primary" :disabled="projectFormSubmitting">{{ projectFormSubmitting ? $t('Save...') : $t('Save') }}</button>
                            <button class="btn text-light" :disabled="projectFormSubmitting" @click.prevent="deactivateUpdateProjectUser">{{ $t('Cancel') }}</button>
                        </form>
                    </ValidationObserver>
                </div>
            </div>

            <!-- Project Overview -->
            <div class="row" v-if="projectFields.plannings.length > 0">
                <div class="col-12 col-sm-6 m-0">
                    <table class="table table-light pt-5" v-if="$can('view_prices') || $can('view_production_time')">
                        <thead class="thead-dark">
                            <tr>
                                <th colspan="2" class="lead">{{ $t('Project overview') }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td class="font-bold">{{ $t('Total length') }}</td>
                                <td>{{ totalProjectLuminationLineLength }} m</td>
                            </tr>
                            <tr v-if="$can('view_production_time')">
                                <td class="font-bold">{{ $t('Production time') }}</td>
                                <td>{{ totalProjectProductionTime }} {{ $t('hours') }}</td>
                            </tr>
                            <tr  v-if="$can('view_prices')">
                                <td class="font-bold">{{ $t('List price') }}</td>
                                <td>€ {{ totalProjectPrice }}</td>
                            </tr>
                            <tr  v-if="$can('view_prices') && discountedTotalProjectPrice !== totalProjectPrice">
                                <td class="font-bold"><strong>{{ $t('Reduced total price') }}</strong></td>
                                <td><strong>€ {{ discountedTotalProjectPrice }}</strong></td>
                            </tr>
                            <tr  v-if="$can('view_purchase_prices') && purchaseprices.total_purchase_price">
                                <td class="font-bold"><strong>{{ $t('Purchase Price') }}</strong></td>
                                <td><strong>€ {{ purchaseprices.total_purchase_price.toFixed(2) }}</strong></td>
                            </tr>
                            <tr  v-if="$can('view_ic_prices') && purchaseprices.total_ic_price">
                                <td class="font-bold"><strong>{{ $t('IC Price') }}</strong></td>
                                <td><strong>€ {{ purchaseprices.total_ic_price.toFixed(2) }}</strong></td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <!-- Luminaire Lines -->
            <div class="row pt-5" v-if="projectFields.plannings.length > 0">
                <div class="col-12 m-0">
                    <h2>{{ $t('Configured products') }}</h2>
                    <div class="row pb-3">
                        <div class="col-12 col-lg-6 m-0">
                            <button @click.prevent="updatePlanningOrder" class="btn btn-sm btn-primary mb-3" v-if="editPlanningOrder">{{ $t('Save order') }}</button>
                            <button @click.prevent="updateEditPlanningOrderStatus" class="btn btn-sm mb-3" :class="!editPlanningOrder ? 'btn-primary' : 'btn-light'">{{ !editPlanningOrder ? $t('Edit order') : $t('Cancel') }}</button>
                            <button @click.prevent="updateShowDeleteMultiplePlannings" class="btn btn-sm mb-3" :disabled="projectFields.project_quote || projectFields.user_id !== user.id" :class="!showDeleteMultiplePlannings ? 'btn-primary' : 'btn-light'">{{ !showDeleteMultiplePlannings ? $t('Delete multiple configurations') : $t('Cancel') }}</button>
                        </div>
                        <div class="col-12 col-lg-6 m-0 text-right">
                            <router-link class="btn btn-sm btn-primary" tag="button" :to="{ name: 'home' }" v-if="!projectFields.project_quote">{{ $t('Configure new product') }}</router-link>
                        </div>
                    </div>
                    <table class="table table-light">
                        <thead class="thead-dark">
                            <tr>
                                <th>{{ $t('No.') }}</th>
                                <th v-if="editPlanningOrder"></th>
                                <th class="lead">{{ $t('Product') }}</th>
                                <th v-if="$can('view_prices')" class="lead">Preis</th>
                                <th v-if="$can('view_purchase_prices')" class="lead">{{ $t('Purchase Price') }}</th>
                                <th v-if="$can('view_ic_prices')" class="lead">{{ $t('IC Price') }}</th>
                                <th class="lead d-none d-sm-table-cell" v-if="$can('view_production_time')">{{ $t('Production time') }}</th>
                                <th class="text-right lead d-none d-sm-table-cell">{{ $t('Actions') }}</th>
                            </tr>
                        </thead>
                        <draggable
                            :list="projectFields.plannings"
                            element="tbody"
                            handle=".fa-grip-horizontal"
                            class="planning-list"
                            animation="400"
                            :disable="!editPlanningOrder" >
                            <tr v-for="(planning, index) in projectFields.plannings">
                                <td><input type="checkbox" v-model="planningsToDelte" :value="planning.id" :disabled="planningsDeleting" class="mr-3" v-if="showDeleteMultiplePlannings">
                                    {{ index + 1 }}.
                                </td>
                                <td v-if="editPlanningOrder"><i class="fas fa-grip-horizontal p-2" style="cursor:move;"></i></td>
                                <td>
                                    <router-link class="font-weight-bold" :to="{ name: 'plannings.show', params: { id: planning.id } }" v-if="planning.id">
                                        {{ planning.title }} ({{ $t('No.') }}: {{ planning.custom_planning_id }})
                                    </router-link><project-invalid class="font-weight-bold" v-if="$planning_needs_update(planning)" title="invalid"/>
                                    <br><span class="small">{{ $t('Type') }}: <product-configurator-title :product_configurator_id="planning.product_configurator_id" :product_configurators="productConfigurators" /> | {{ $t('Created') }}: {{ planning.created_at }} | Länge: {{ planning.total_lumination_line_length > 0 ? (planning.total_lumination_line_length / 100).toFixed(2) : 0 }} m</span></td>
                                <td v-if="$can('view_prices')">€ {{ planning.total_price_of_lumination_lines !== planning.reduced_total_price_of_lumination_lines && planning.reduced_total_price_of_lumination_lines ? planning.reduced_total_price_of_lumination_lines.toFixed(2) : planning.total_price_of_lumination_lines.toFixed(2) }}</td>
                                <td v-if="$can('view_purchase_prices') && purchaseprices.planning_purchase_prices">€ {{ purchaseprices.planning_purchase_prices[planning.id] ? purchaseprices.planning_purchase_prices[planning.id].toFixed(2) : '0.00' }}</td>
                                <td v-if="$can('view_ic_prices') && purchaseprices.planning_ic_prices">€ {{ purchaseprices.planning_ic_prices[planning.id] ? purchaseprices.planning_ic_prices[planning.id].toFixed(2) : '0.00' }}</td>
                                <td class="d-none d-sm-table-cell" v-if="$can('view_production_time')">{{ planning.total_production_time_of_lumination_lines.toFixed(2) }} {{ $t('hours') }}</td>
                                <td class="text-right d-none d-sm-table-cell">
                                    <router-link class="btn btn-sm btn-primary text-white" tag="a" :to="{ name: 'plannings.show', params: { id: planning.id } }" v-if="planning.id"><i class="far fa-eye" title="Öffnen"></i> <span>{{ $t('Open') }}</span></router-link>
                                    <div class="dropdown d-inline-block">
                                        <button class="btn btn-primary btn-sm dropdown-toggle" type="button" :id="'project-edit-button-' + planning.id" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false"
                                                :disabled="$planning_needs_update(planning) || projectFields.project_quote || projectFields.project_finished || projectFields.user_id !== user.id">
                                            <i class="far fa-edit"></i> {{ $t('Edit') }}
                                        </button>
                                        <div class="dropdown-menu" :aria-labelledby="'project-edit-button-' + planning.id">
                                            <router-link class="dropdown-item" tag="button" :to="{ name: 'plannings.edit', params: { id: planning.id } }"
                                                         v-if="planning.id"
                                                         :disabled="$planning_needs_update(planning) || projectFields.project_quote || projectFields.project_finished || projectFields.user_id !== user.id">
                                                {{ !planning.total_lumination_line_length ? $t('Continue configuration') : $t('Edit configuration') }}
                                            </router-link >
                                            <router-link class="dropdown-item" tag="button" :to="{ name: 'plannings.arrange', params: { id: planning.id } }"
                                                         v-if="planning.id && planning.total_lumination_line_length && planning.total_lumination_line_length > 0"
                                                         :disabled="$planning_needs_update(planning) || projectFields.project_quote || projectFields.project_finished || projectFields.user_id !== user.id">
                                                {{ $t('Edit arrangement') }}
                                            </router-link >
                                        </div>
                                    </div>
                                    <button class="btn btn-sm btn-primary" @click.prevent="duplicatePlanningMultiple(planning.id)"
                                            :disabled="$planning_needs_update(planning) || planningDuplicating || projectFields.project_quote || projectFields.project_finished || projectFields.user_id !== user.id">
                                        <i class="far fa-clone" :title="$t('Duplicate')"></i> <span>{{ $t('Duplicate') }}</span>
                                    </button><br>
                                    <div class="dropdown d-inline-block">
                                        <button class="btn btn-primary btn-sm dropdown-toggle" type="button" :id="'project-export-button-' + planning.id" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                                            <i class="fas fa-cloud-download-alt"></i> Export
                                        </button>
                                        <div class="dropdown-menu" :aria-labelledby="'project-edit-button-' + planning.id">
                                            <download-pdf-button
                                                :url="'/pdf/plannings/' + planning.id"
                                                :button_class="'dropdown-item'" />
                                            <download-excel-button
                                                :disabled="$planning_needs_update(planning)"
                                                :url="'/excel/plannings/' + planning.id"
                                                :button_class="'dropdown-item'" />
                                            <download-excel-button v-if="$can('download_mounting_rail_labels_data') && planning.product_configurator_id === 1"
                                                :button_text="$t('Export labels')"
                                                :price_option="false"
                                                :production_time_option="false"
                                                :url="'/excel/plannings/' + planning.id + '/labels'"
                                                :button_class="'dropdown-item'" />
                                            <download-dialux-data-button
                                                v-if="planning.total_lumination_line_length && planning.total_lumination_line_length > 0"
                                                :plannings="[planning]"
                                                :button_class="'dropdown-item'"
                                                @update-loading="updateLoading" />
                                            <download-tender-text-button
                                                :product_configurator_id="planning.product_configurator_id"
                                                :planning_data="planning"
                                                :planning_id="planning.id"
                                                :button_class="'dropdown-item'"
                                                @update-loading="updateLoading" />
                                        </div>
                                    </div>
                                    <button class="btn btn-sm btn-danger" @click.prevent="deletePlanning(planning.id)" :disabled="planningDeleting || projectFields.project_quote || projectFields.user_id !== user.id"><i class="far fa-trash-alt" :title="$t('Delete')"></i></button>
                                </td>
                            </tr>
                        </draggable>
                        <tfoot class="thead-dark">
                            <tr>
                                <th></th>
                                <th v-if="editPlanningOrder"></th>
                                <th v-if="!$can('view_prices') && !$can('view_production_time')"></th>
                                <th v-if="$can('view_prices') || $can('view_production_time')">{{ $t('TOTAL') }}</th>
                                <th v-if="$can('view_prices')">€ {{ discountedTotalProjectPrice }}</th>
                                <th v-if="$can('view_purchase_prices') && purchaseprices.total_purchase_price">€ {{ purchaseprices.total_purchase_price.toFixed(2) }}</th>
                                <th v-if="$can('view_ic_prices') && purchaseprices.total_ic_price">€ {{ purchaseprices.total_ic_price.toFixed(2) }}</th>
                                <th class="d-none d-sm-table-cell" v-if="$can('view_production_time')">{{ totalProjectProductionTime }} {{ $t('hours') }}</th>
                                <th class="d-none d-sm-table-cell text-right"><button class="btn btn-sm btn-danger" @click.prevent="deleteSelectedPlannings()" v-if="showDeleteMultiplePlannings" :disabled="planningDeleting || planningsDeleting || planningsToDelte < 1 || projectFields.project_quote || projectFields.user_id !== user.id"><i class="far fa-trash-alt" :title="$t('Delete selected configurations')"></i> {{ $t('Delete selected configurations') }}</button></th>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>
            <div v-else>
                <div class="alert alert-warning" role="alert">
                    {{ $t('No systems configured yet') }}
                </div>
                <router-link :to="{ name: 'templed-rayline' }" tag="button" class="btn btn-primary" v-if="projectFields.user_id === user.id"><i class="fas fa-cogs"></i> {{ $t('Configure line lighting system now') }}</router-link>
            </div>

            <!--  Delte Project -->
            <div class="row pt-5" v-if="projectFields.user_id === user.id">
                <div class="col-12 m-0">
                    <button class="btn btn-outline-danger" @click.prevent="deleteProject" :disabled="projectDeleting">{{ projectDeleting ? $t('Delete project') + '...' : $t('Delete project') }}</button>
                </div>
            </div>

        </div>
        <div class="container content-page-wrapper" v-else :class="{'loading': loading}">
            <div class="row py-5">
                <div class="col-12">
                    <display-error-messages v-if="!loading"
                        :error="loadingError"
                        :title="$t('Project')" />
                </div>
            </div>
        </div>
    </div>
</template>

<script>

import {mapGetters} from "vuex";
import draggable from 'vuedraggable';
import Select2 from 'v-select2-component';
import ProductConfiguratorTitle from "../Utils/Titles/ProductConfiguratorTitle.vue";
import ProjectInvalid from "../Utils/Errors/ProjectInvalid.vue";

export default {

    components: {
        ProjectInvalid,
        ProductConfiguratorTitle,
        draggable
    },

    data() {
        return {
            loading: false,
            projectFields: {

            },
            loadingError: null,
            projectFormErrors: {},
            projectFormSubmitting: false,
            editProject: false,
            editProjectUser: false,
            requestQuoteSubmitting: false,
            projectDeleting: false,
            planningDeleting: false,
            planningsDeleting: false,
            showDeleteMultiplePlannings: false,
            planningDuplicating: false,
            dialuxFilePath: [],
            submitOrderSubmitting: false,
            projectDuplicating: false,
            editPlanningOrder: false,
            planningsOrder: null,
            users: [],
            projectOlderThan90Days: '',
            productConfigurators: [],
            planningsToDelte: [],
            purchaseprices: 0,
        };
    },

    created() {
        this.getProductConfigurators();
        this.getProject();
    },

    methods: {
        updateLoading(value) {
            this.loading = value;
        },
        activateEditProject() {
            this.editProject = true;
        },
        deactivateEditProject() {
            this.editProject = false;
            this.getProject();
        },
        activateUpdateProjectUser() {
            this.getUsers();
            this.editProjectUser = true;
        },
        deactivateUpdateProjectUser() {
            this.editProjectUser = false;
            this.getProject();
        },

        checkIfProjectOlderThan90Days() {
            const today = new Date();
            const updatedAt = new Date(this.projectFields.updated_at);
            this.projectOlderThan90Days = +updatedAt < +today - 90 * 24 * 60 * 60 * 1000;
        },

        updateEditPlanningOrderStatus() {
            this.editPlanningOrder = !this.editPlanningOrder;
            if (this.editPlanningOrder) {
                this.setPlanningOrder();
            } else {
                this.getProject();
            }
        },

        setPlanningOrder() {
            this.planningsOrder = this.projectFields.plannings.map(function(planning, index) {
                return { planning_id: planning.id, planning_order: index + 1 }
            })
        },

        updatePlanningOrder() {
            this.loading = true;
            this.projectFormSubmitting = true;
            axios.put('/api/projects/' + this.$route.params.id + '/update_plannings_order', this.planningsOrder )
                .then(response => {
                    this.$swal.fire({
                        icon: 'success',
                        title: this.$t('Updated successfully'),
                        confirmButtonColor: '#3CA2A4'
                    });
                    this.projectFormSubmitting = false;
                    this.editPlanningOrder = false;
                    this.getProject();
                    this.loading = false;
                }).catch(error => {
                this.$swal.fire({
                    icon: 'error',
                    title: this.$t('There was an error'),
                    text: this.$t('Please check the form'),
                    confirmButtonColor: '#3CA2A4'
                });
                if (error.response.status === 422) { // Validation Error
                    console.log(error.response.data.errors);
                }
                this.projectFormSubmitting = false;
                this.loading = false;
            });
        },

        getUsers() {
            this.loading = true;
            axios.get('/api/users?select2=true')
                .then(response => {
                    this.users = response.data;
                    this.loading = false;
                }).catch(error => {
                this.loading = false;
            });
        },

        getProductConfigurators() {
            axios.get('/api/product_configurators')
                .then(response => {
                    this.productConfigurators = response.data.data;
                }).catch(error => {
                this.loadingError = error.response;
            });
        },

        select2Change(val) {
            console.log('change', val);
        },
        select2Selected() {
            console.log('select', val);
        },

        getProject() {
            this.loading = true;
            axios.get('/api/projects/' + this.$route.params.id + '?paginate=0')
                .then(response => {
                    this.projectFields = response.data.data;
                    this.checkIfProjectOlderThan90Days();
                    if (this.$can('view_purchase_prices') || this.$can('view_ic_prices')) {
                        this.getProjectPurchasePrices();
                    }
                    this.loading = false;
                }).catch(error => {
                    this.loadingError = error.response;
                    this.loading = false;
            });
        },

        updateProject() {
            this.projectFormSubmitting = true;
            axios.put('/api/projects/' + this.$route.params.id, { title: this.projectFields.title } )
                .then(response => {
                    this.$swal.fire({
                        icon: 'success',
                        title: this.$t('Updated successfully'),
                        confirmButtonColor: '#3CA2A4'
                    });
                    this.projectFormSubmitting = false;
                    this.editProject = false;
                    this.getProject();
                }).catch(error => {
                this.$swal.fire({
                    icon: 'error',
                    title: this.$t('There was an error'),
                    text: this.$t('Please check the form'),
                    confirmButtonColor: '#3CA2A4'
                });
                if (error.response.status === 422) { // Validation Error
                    this.projectFormErrors = error.response.data.errors;
                }
                this.projectFormSubmitting = false;
            });
        },

        updateProjectUser () {
            this.projectFormSubmitting = true;
            this.loading = true;
            axios.put('/api/projects/' + this.$route.params.id + '/users/' + this.projectFields.user_id + '/update_project_user')
                .then(response => {
                    this.$swal.fire({
                        icon: 'success',
                        title: this.$t('Updated successfully'),
                        confirmButtonColor: '#3CA2A4'
                    });
                    this.projectFormSubmitting = false;
                    this.editProjectUser = false;
                    this.getProject();
                    this.loading = false;
                }).catch(error => {
                    this.loading = false;
                    this.$swal.fire({
                        icon: 'error',
                        title: this.$t('There was an error'),
                        text: this.$t('Please check teh form'),
                        confirmButtonColor: '#3CA2A4'
                    });
                if (error.response.status === 422) { // Validation Error
                    this.projectFormErrors = error.response.data.errors;
                }
                this.loading = false;
                this.editProjectUser = false;
                this.projectFormSubmitting = false;
            });
        },

        duplicateProject(project_id) {

            this.$swal.fire({
                title: this.$t('Project name'),
                text: this.$t('Please enter the name of the project'),
                icon: 'question',
                showCancelButton: true,
                confirmButtonColor: '#3CA2A4',
                cancelButtonColor: '#393e42',
                confirmButtonText: this.$t('Duplicate'),
                cancelButtonText: this.$t('Cancel'),
                input: 'text',
                inputValidator: (value) => {
                    if ( !value ) {
                        return this.$t('Please enter a valid name')
                    }
                    if ( typeof value !== 'string' ) {
                        return this.$t('Please enter a valid name without any special characters')
                    }
                }
            }).then((result) => {

                if (result.value) {
                    this.projectDuplicating = true;
                    this.loading = true;
                    let projectTitle = { title: result.value };

                    axios.put('/api/projects/' + project_id + '/duplicate', projectTitle)
                        .then(response => {
                            this.$swal.fire({
                                icon: 'success',
                                title: this.$t('Duplicated successfully'),
                                confirmButtonColor: '#3CA2A4'
                            });
                            this.projectDuplicating = false;
                            this.loading = false;
                        }).catch(error => {
                            console.log(error);
                            this.$swal.fire({icon: 'error', title: this.$t('There was an error'), confirmButtonColor: '#3CA2A4'});
                            this.projectDuplicating = false;
                            this.loading = false;
                    });
                }
            })
        },

        deleteProject() {
            this.$swal.fire({
                title: this.$t('Are you sure?'),
                text: this.$t("After deleting the project, the data can not be restored."),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#e3342f',
                cancelButtonColor: '#393e42',
                confirmButtonText: this.$t("Yes, delete!"),
                cancelButtonText: this.$t("Cancel")
            }).then((result) => {
                if (result.value) {
                    this.projectDeleting = true;
                    this.loading = true;

                    axios.delete('/api/projects/' + this.$route.params.id)
                        .then(response => {
                            this.$swal.fire({
                                icon: 'success',
                                title: this.$t('Deleted successfully')
                            });
                            this.projectDeleting = false;
                            this.loading = false;
                            this.$router.push({ name: 'projects.index' });
                        }).catch(error => {
                            this.$swal.fire({ icon: 'error', title: this.$t('There was an error')});
                            this.loading = false;
                    });

                }
            })
        },

        deletePlanning(planning_id) {
            this.$swal.fire({
                title: this.$t('Are you sure?'),
                text: this.$t("After deleting the lighting line, the data can not be restored."),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#e3342f',
                cancelButtonColor: '#393e42',
                confirmButtonText: this.$t("Yes, delete!"),
                cancelButtonText: this.$t("Cancel!")
            }).then((result) => {
                if (result.value) {
                    this.planningDeleting = true;
                    this.loading = true;

                    axios.delete('/api/plannings/' + planning_id)
                        .then(response => {
                            this.$swal.fire({
                                icon: 'success',
                                title: this.$t('Deleted successfully')
                            });
                            this.planningDeleting = false;
                            this.getProject();
                            this.loading = false;
                        }).catch(error => {
                            this.$swal.fire({ icon: 'error', title: this.$t('There was an error')});
                            this.planningDeleting = false;
                            this.loading = false;
                    });

                }
            })
        },

        updateShowDeleteMultiplePlannings() {
            this.showDeleteMultiplePlannings = !this.showDeleteMultiplePlannings;
            this.planningsToDelte = [];
        },

        async deleteSelectedPlannings() {
            const result = await this.$swal.fire({
                title: this.$t('Are you sure?'),
                text: this.$t("After deleting these configurations, the data can not be restored."),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#e3342f',
                cancelButtonColor: '#393e42',
                confirmButtonText: this.$t("Yes, delete!"),
                cancelButtonText: this.$t("Cancel!")
            });

            if (result.value) {
                this.planningsDeleting = true;
                this.loading = true;

                let deletedPlannings = [];
                let deletePlanningsErrors = [];

                await Promise.all(this.planningsToDelte.map(async planning_id => {
                    try {
                        await axios.delete('/api/plannings/' + planning_id);
                        deletedPlannings.push(planning_id);
                    } catch (error) {
                        deletePlanningsErrors.push(planning_id);
                    }
                }));

                if (deletedPlannings.length < 1 && deletePlanningsErrors.length < 1) {
                    this.$swal.fire({ icon: 'info', title: this.$t('No configurations were deleted')});
                } else if (deletedPlannings.length < 1 && deletePlanningsErrors.length > 0) {
                    this.$swal.fire({ icon: 'error', title: this.$t('There was an error. No configurations were deleted')});
                } else if (this.planningsToDelte.length === deletedPlannings.length) {
                    this.$swal.fire({ icon: 'success', title: this.$t('Deleted successfully')});
                } else {
                    this.$swal.fire({ icon: 'warning', title: this.$t('Some configurations were not deleted')});
                }

                this.getProject();
                this.planningsToDelte = [];
                this.planningsDeleting = false;
                this.showDeleteMultiplePlannings = false;
                this.loading = false;
            }
        },


        duplicatePlanning(planning_id) {

            this.$swal.fire({
                title: this.$t('Name of lighting line'),
                text: this.$t('Please enter the name of your lighting line '),
                icon: 'question',
                showCancelButton: true,
                confirmButtonColor: '#3CA2A4',
                cancelButtonColor: '#393e42',
                confirmButtonText: this.$t('Duplicate'),
                cancelButtonText: this.$t('Cancel'),
                input: 'text',
                inputValidator: (value) => {
                    if ( !value ) {
                        return this.$t('Please enter a valid name')
                    }
                    if ( typeof value !== 'string' ) {
                        return this.$t('Please enter a valid name without any special characters')
                    }
                }
            }).then((result) => {

                if (result.value) {
                    this.planningDuplicating = true;
                    let planningTitle = { title: result.value };

                    axios.put('/api/plannings/' + planning_id + '/duplicate', planningTitle)
                        .then(response => {
                            this.$swal.fire({
                                icon: 'success',
                                title: this.$t('Duplicated successfully'),
                                confirmButtonColor: '#3CA2A4'
                            });
                            this.planningDuplicating = false;
                            this.getProject();
                        }).catch(error => {
                        this.$swal.fire({icon: 'error', title: this.$t('There was an error'), confirmButtonColor: '#3CA2A4'});
                        this.planningDuplicating = false;
                    });
                }
            })
        },

        duplicatePlanningMultiple(planning_id) {
            this.$swal.fire({
                title: this.$t('Duplicates'),
                text: this.$t('Please enter the number of duplicates'),
                icon: 'question',
                showCancelButton: true,
                confirmButtonColor: '#3CA2A4',
                cancelButtonColor: '#393e42',
                confirmButtonText: this.$t('Duplicate'),
                cancelButtonText: this.$t('Cancel'),
                input: 'range',
                inputAttributes: {
                    min: 1,
                    max: 20,
                    step: 1
                },
                inputValue: 1,
                inputValidator: (value) => {
                    if ( !value ) {
                        return this.$t('Please enter a valid number')
                    }
                    if ( value < 1 || value > 20 ) {
                        return this.$t('Please enter a number between 1 and 20')
                    }
                }
            }).then((result) => {
                if (result.value) {
                    this.planningDuplicating = true;
                    this.loading = true;
                    let planningQuantity = { quantity: result.value };

                    axios.put('/api/plannings/' + planning_id + '/duplicate_multiple', planningQuantity)
                        .then(response => {
                            this.$swal.fire({
                                icon: 'success',
                                title: this.$t('Duplicated successfully'),
                                confirmButtonColor: '#3CA2A4'
                            });
                            this.planningDuplicating = false;
                            this.getProject();
                            this.loading = false;
                        }).catch(error => {
                        this.$swal.fire({icon: 'error', title: this.$t('There was an error'), confirmButtonColor: '#3CA2A4'});
                        this.planningDuplicating = false;
                        this.loading = false;
                    });
                }
            })
        },

        requestQuote() {

            this.$swal.fire({
                title: this.$t('Non-binding request'),
                text: this.$t('Would you like to send a non-binding request for an offer to tempLED for this project?'),
                icon: 'question',
                showCancelButton: true,
                confirmButtonColor: '#3CA2A4',
                cancelButtonColor: '#393e42',
                confirmButtonText: this.$t('Yes'),
                cancelButtonText: this.$t('Cancel')
            }).then((result) => {

                if (result.value) {
                    this.projectFormSubmitting = true;

                    axios.put('/api/projects/' + this.$route.params.id + '/request_quote')
                        .then(response => {
                            this.$swal.fire({
                                icon: 'success',
                                title: this.$t('Sent successfully'),
                                text: this.$t('We will respond to your request as soon as possible'),
                                confirmButtonColor: '#3CA2A4'
                            });
                            this.projectFormSubmitting = false;
                            this.getProject();
                        }).catch(error => {
                        this.$swal.fire({icon: 'error', title: this.$t('There was an error'), confirmButtonColor: '#3CA2A4'});
                        this.projectFormSubmitting = false;
                    });
                }
            })
        },

        submitOrder() {

            this.$swal.fire({
                title: this.$t('Order project'),
                text: this.$t('Would you like to order this project with tempLED?'),
                icon: 'question',
                showCancelButton: true,
                confirmButtonColor: '#3CA2A4',
                cancelButtonColor: '#393e42',
                confirmButtonText: this.$t('Yes, order now'),
                cancelButtonText: this.$t('Cancel')
            }).then((result) => {

                if (result.value) {
                    this.submitOrderSubmitting = true;

                    axios.put('/api/projects/' + this.$route.params.id + '/order_project')
                        .then(response => {
                            this.$swal.fire({
                                icon: 'success',
                                title: this.$t('Ordered successfully'),
                                text: this.$t('We will work on your order as quickly as possible'),
                                confirmButtonColor: '#3CA2A4'
                            });
                            this.submitOrderSubmitting = false;
                            this.getProject();
                        }).catch(error => {
                        this.$swal.fire({icon: 'error', title: this.$t('There was an error'), confirmButtonColor: '#3CA2A4'});
                        this.submitOrderSubmitting = false;
                    });
                }
            })
        },

        setProjectAsFinished() {

            this.$swal.fire({
                title: this.$t('Finish project'),
                text: this.$t('Would you like to mark this project as finished?'),
                icon: 'question',
                showCancelButton: true,
                confirmButtonColor: '#3CA2A4',
                cancelButtonColor: '#393e42',
                confirmButtonText: this.$t('Yes'),
                cancelButtonText: this.$t('Cancel')
            }).then((result) => {

                if (result.value) {
                    this.loading = true;

                    axios.put('/api/projects/' + this.$route.params.id + '/finish_project')
                        .then(response => {
                            this.$swal.fire({
                                icon: 'success',
                                title: this.$t('Successfully marked as finished'),
                                confirmButtonColor: '#3CA2A4'
                            });
                            this.loading = false;
                            this.getProject();
                        }).catch(error => {
                        this.$swal.fire({icon: 'error', title: this.$t('There was an error'), confirmButtonColor: '#3CA2A4'});
                        this.loading = false;
                    });
                }
            })
        },

        resetProjectStatus() {

            this.$swal.fire({
                title: this.$t('Reset project status'),
                text: this.$t('Would you like to mark this project as active and remove all other status?'),
                icon: 'question',
                showCancelButton: true,
                confirmButtonColor: '#3CA2A4',
                cancelButtonColor: '#393e42',
                confirmButtonText: this.$t('Yes'),
                cancelButtonText: this.$t('Cancel')
            }).then((result) => {

                if (result.value) {
                    this.loading = true;

                    axios.put('/api/projects/' + this.$route.params.id + '/reset_project_status')
                        .then(response => {
                            this.$swal.fire({
                                icon: 'success',
                                title: this.$t('Successfully marked as finished'),
                                confirmButtonColor: '#3CA2A4'
                            });
                            this.loading = false;
                            this.getProject();
                        }).catch(error => {
                        this.$swal.fire({icon: 'error', title: this.$t('There was an error'), confirmButtonColor: '#3CA2A4'});
                        this.loading = false;
                    });
                }
            })
        },

        updateDeletionDeadline() {

            this.$swal.fire({
                title: this.$t('Extend deletion deadline'),
                text: this.$t('Would you like to prolong the deletion deadline for another 3 months?'),
                icon: 'question',
                showCancelButton: true,
                confirmButtonColor: '#3CA2A4',
                cancelButtonColor: '#393e42',
                confirmButtonText: this.$t('Yes'),
                cancelButtonText: this.$t('Cancel')
            }).then((result) => {

                if (result.value) {
                    this.loading = true;

                    axios.put('/api/projects/' + this.$route.params.id + '/update_project_deletion_deadline')
                        .then(response => {
                            this.$swal.fire({
                                icon: 'success',
                                title: this.$t('Extended successfully'),
                                confirmButtonColor: '#3CA2A4'
                            });
                            this.loading = false;
                            this.getProject();
                        }).catch(error => {
                        this.$swal.fire({icon: 'error', title: this.$t('There was an error'), confirmButtonColor: '#3CA2A4'});
                        this.loading = false;
                    });
                }
            })
        },

        getProjectPurchasePrices() {
            this.loading = true;
            axios.get('/api/projects/' + this.$route.params.id + '/total_project_purchase_prices')
                .then(response => {
                    this.purchaseprices = response.data;
                    this.loading = false;
                    console.log(this.purchaseprices);
                }).catch(error => {
                this.loadingError = error.response;
            });
        },

	},

    computed: {
        ...mapGetters({
            authenticated: 'auth/authenticated',
            user: 'auth/user',
            permissions: 'auth/permissions'
        }),

        totalProjectPrice() {
            var sum = 0;
            if ( this.projectFields.plannings ) {
                this.projectFields.plannings.map( function(element) {
                    if ( element.total_price_of_lumination_lines) {
                        sum += element.total_price_of_lumination_lines
                    }
                });
            }
            return sum.toFixed(2);;
        },
        discountedTotalProjectPrice() {
            var sum = 0;
            if ( this.projectFields.plannings ) {
                this.projectFields.plannings.map( function(element) {
                    if ( element.reduced_total_price_of_lumination_lines && element.reduced_total_price_of_lumination_lines > 0 && element.reduced_total_price_of_lumination_lines !== element.total_price_of_lumination_lines) {
                        sum += element.reduced_total_price_of_lumination_lines
                    } else if ( element.total_price_of_lumination_lines) {
                        sum += element.total_price_of_lumination_lines
                    }
                });
            }
            return sum.toFixed(2);;
        },

        totalProjectProductionTime() {
            var sum = 0;
            if ( this.projectFields.plannings ) {
                this.projectFields.plannings.map(function(x){
                    if (x.total_production_time_of_lumination_lines) {
                        sum += x.total_production_time_of_lumination_lines;
                    }
                });
            }
            return sum.toFixed(2);
        },

        totalProjectLuminationLineLength() {
            var sum = 0;
            if ( this.projectFields.plannings ) {
                this.projectFields.plannings.map(function(x){
                    if (x.total_lumination_line_length) {
                        sum += x.total_lumination_line_length;
                    }
                });
            }
            return (sum / 100).toFixed(2);
        }
    },

    watch: {
        projectFields: {
            handler(val, oldVal){
                this.setPlanningOrder();
            },
            deep: true
        },
    }
};
</script>
